// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { Text } from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskPunttArtifactProvider } from "../../components/ask-puntt-review";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { useAppSelector } from "../../utils/hooks";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_HOME_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import { collectRailContent, hasRailContent } from "../rail/collectRailContent";
import { type AskV2RailSectionId } from "../rail/types";
import { AskV2ReviewWorkspace } from "../review/AskV2ReviewWorkspace";
import { useAskV2ReviewArtifactContext } from "../review/useAskV2ReviewArtifactContext";
import { useAskV2ReviewSidebarAutoCollapse } from "../review/useAskV2ReviewSidebarAutoCollapse";
import { useAskV2ReviewWorkspace } from "../review/useAskV2ReviewWorkspace";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ChatPageProps = {
  chatId: string;
  reviewId?: string;
  commentId?: string;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ChatPage({
  chatId,
  reviewId,
  commentId,
}: AskV2ChatPageProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const currentUser = useAppSelector((state) => state.auth.value);
  const currentUserId = currentUser?.userID;
  const currentUserName =
    currentUser?.name?.trim() || currentUser?.email?.trim();
  const transport = useAskV2Transport({ chatId, presentation: "chat" });
  const title = transport.chatTitle?.trim() || t("askV2.pages.chat.title");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const workspace = useAskV2ReviewWorkspace({
    scope: { kind: "chat", chatId },
    reviewId,
    commentId,
  });
  const railContent = useMemo(
    () =>
      collectRailContent({
        chat: transport.historyChat,
        messages: transport.historyMessages,
      }),
    [transport.historyChat, transport.historyMessages],
  );
  const railHasContent = hasRailContent(railContent);
  const showRailToggle = !workspace.isReviewOpen && railHasContent;
  const showRail = showRailToggle && railOpen;
  const versionOptions = useMemo(() => {
    const reviewIds = transport.historyChat?.reviewIds ?? [];
    return reviewIds.map((id, index) => ({
      reviewId: id,
      title: t("askV2.review.versionLabel", { number: index + 1 }),
    }));
  }, [transport.historyChat?.reviewIds, t]);
  const artifactContext = useAskV2ReviewArtifactContext({
    workspace,
    messages: transport.historyMessages,
  });
  useAskV2ReviewSidebarAutoCollapse(workspace.isReviewOpen);

  return (
    <AskV2ConversationShell
      testId="ask-v2-chat-page"
      fullBleed={workspace.isReviewOpen}
      onAddQuotedContext={
        workspace.isReviewOpen ? undefined : transport.addQuotedContext
      }
      header={
        <PageHeader>
          <BreadcrumbHeaderTrail className="min-w-0 flex-1 overflow-hidden">
            <BreadcrumbHeaderRoot
              to={ASK_V2_HOME_PATH}
              label={tCommon("nav.askPuntt")}
              testId="ask-v2-chat-breadcrumb-root"
            />
            <span
              className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
              data-testid="ask-v2-chat-title"
            >
              {title}
            </span>
            <span
              className={`${BREADCRUMB_HEADER_CHIP_CLASS} sr-only`}
              data-testid="ask-v2-chat-id"
            >
              {chatId}
            </span>
          </BreadcrumbHeaderTrail>
          {showRailToggle ? (
            <AskV2RailToggle
              open={railOpen}
              onToggle={() => setRailOpen((current) => !current)}
            />
          ) : null}
        </PageHeader>
      }
      composer={
        workspace.isReviewOpen ? undefined : (
          <AskV2SummonBar transport={transport} />
        )
      }
      rail={
        showRail ? (
          <AskV2RightRail
            content={railContent}
            sectionOpen={sectionOpen}
            onReviewOpen={workspace.openReview}
            onSectionOpenChange={(id, open) =>
              setSectionOpen((current) => ({ ...current, [id]: open }))
            }
          />
        ) : null
      }
    >
      {workspace.isReviewOpen ? (
        <AskV2ReviewWorkspace
          workspace={workspace}
          transport={transport}
          versionOptions={versionOptions}
          feedPresentation="chat"
          feedActorName={currentUserName}
          currentUserId={currentUserId}
          artifactContext={artifactContext}
          composer={<AskV2SummonBar transport={transport} />}
        />
      ) : (
        <AskPunttArtifactProvider value={artifactContext}>
          {transport.historyLoading && transport.messages.length === 0 ? (
            <AskV2ThreadLoading />
          ) : transport.historyError && transport.messages.length === 0 ? (
            <Text
              as="p"
              size="2"
              color="gray"
              data-testid="ask-v2-thread-error"
              className="py-8 text-center"
            >
              {t("askV2.pages.chat.loadError")}
            </Text>
          ) : (
            <AskV2Thread transport={transport} presentation="chat" />
          )}
        </AskPunttArtifactProvider>
      )}
    </AskV2ConversationShell>
  );
}
